import React from 'react';
import WeeklyTimeTable from '../common/WeeklyTimeTable';
import SearchableDropdown from '../common/SearchableDropdown';
import { STATE_OPTIONS, STATE_DISTRICTS } from '@tutor/types';
import type { ParentRegistrationFormData } from '../../types';
import { useOptionalI18n } from '@/i18n';
import { localizeText } from '@/i18n/options';
import { getWeeklyTimeTableI18nProps } from '@/utils/weeklyTimeTableI18n';

interface LocationTimeStepProps {
  formData: ParentRegistrationFormData;
  errors: Record<string, string>;
  onChange: (formData: Partial<ParentRegistrationFormData>) => void;
}

const LocationTimeStep: React.FC<LocationTimeStepProps> = ({
  formData,
  errors,
  onChange,
}) => {
  const { locale } = useOptionalI18n();
  const isOnline = formData.tuitionType === 'online';
  const weeklyTimeTableI18n = getWeeklyTimeTableI18nProps(locale);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-1 h-5 bg-accent rounded-full" />
        <h3 className="text-lg font-semibold text-gray-900">{localizeText(locale, 'Location & Preferred Time')}</h3>
      </div>
      <div className="space-y-4">
        {/* Location fields hidden for online tuition */}
        {!isOnline && (
          <>
            {/* Layer 1: State */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {localizeText(locale, 'State')} <span className="text-red-500">*</span>
              </label>
              <SearchableDropdown
                options={STATE_OPTIONS}
                value={formData.state}
                onChange={(value) =>
                  onChange({ state: String(value), district: '', areaNote: '' })
                }
                placeholder={localizeText(locale, 'Select State')}
                searchable={true}
                className={errors.state ? 'border-red-500' : ''}
              />
              {errors.state && (
                <p className="text-red-500 text-sm mt-1">{errors.state}</p>
              )}
            </div>

            {/* Layer 2: District */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {localizeText(locale, 'District')} <span className="text-red-500">*</span>
              </label>
              <SearchableDropdown
                options={STATE_DISTRICTS[formData.state as keyof typeof STATE_DISTRICTS]?.map((d) => ({ value: d, label: d })) ?? []}
                value={formData.district}
                onChange={(value) => onChange({ district: String(value) })}
                placeholder={localizeText(locale, 'Select a district...')}
                searchable={true}
                disabled={!formData.state}
                className={errors.district ? 'border-red-500' : ''}
              />
              {errors.district && (
                <p className="text-red-500 text-sm mt-1">{errors.district}</p>
              )}
            </div>

            {/* Layer 3: Area Note */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {localizeText(locale, 'Specific Area / Neighbourhood')} <span className="text-gray-400 font-normal">({localizeText(locale, 'Optional')})</span>
              </label>
              <input
                type="text"
                placeholder={localizeText(locale, 'e.g. Taman Desa, Bangsar Baru, Sri Hartamas...')}
                className={`input-field ${errors.areaNote ? 'border-red-500' : ''}`}
                value={formData.areaNote}
                onChange={(e) => onChange({ areaNote: e.target.value })}
                disabled={!formData.state}
              />
            </div>
          </>
        )}

        <WeeklyTimeTable
          selectedTimes={formData.preferredTimes}
          onChange={(preferredTimes) =>
            onChange({ preferredTimes })
          }
          label={localizeText(locale, 'Available Times')}
          helperText={localizeText(locale, 'Click on the grid to select your preferred times')}
          colorTheme="secondary"
          {...weeklyTimeTableI18n}
        />
        {errors.preferredTimes && (
          <p className="text-red-500 text-sm mt-2">{errors.preferredTimes}</p>
        )}
      </div>
    </div>
  );
};

export default LocationTimeStep;
